import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { TFunction } from 'i18next';
import { BudgetSiSClassification } from '@callmart/types';
import { BillingConfigWithRelations } from '../../services/budgetBillingConfig';

// Shared identifier columns + row fields for the three billing grids (Minute Prices, Contract ABT,
// Bonus). They all describe the same billing config, so the leading Partner / Standort / Working
// Unit / SiS-Service columns are defined once here and reused (CALL-751).
// i18n-ns: budget (callers pass their budget-namespace `t`)

// CALL-751: the SiS/Service classification rides along as a small badge next to the Working Unit
// name, so it's visible at a glance without spending a whole column. Color-coded per class.
const SIS_BADGE_COLORS: Record<string, { bg: string; color: string }> = {
    [BudgetSiSClassification.SERVICE]: { bg: '#e3f2fd', color: '#1565c0' },
    [BudgetSiSClassification.SALES]: { bg: '#e8f5e9', color: '#2e7d32' },
    [BudgetSiSClassification.SIS]: { bg: '#fff3e0', color: '#e65100' },
    [BudgetSiSClassification.RETAIN]: { bg: '#f3e5f5', color: '#6a1b9a' },
};

const FALLBACK_BADGE_COLOR = { bg: '#eeeeee', color: '#616161' };

const WorkingUnitCell = (params: ICellRendererParams) => {
    const name = (params.value as string) ?? '';
    const sis = (params.data?.sisClassification as string) || '';
    const colors = SIS_BADGE_COLORS[sis] ?? FALLBACK_BADGE_COLOR;

    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span>{name}</span>
            {sis ? (
                <span
                    style={{
                        padding: '0 6px',
                        borderRadius: 8,
                        fontSize: 11,
                        lineHeight: '16px',
                        fontWeight: 600,
                        backgroundColor: colors.bg,
                        color: colors.color,
                    }}
                >
                    {sis}
                </span>
            ) : null}
        </span>
    );
};

/** Build the leading identifier fields of a grid row from a billing config. */
export const buildIdentifierRow = (config: BillingConfigWithRelations) => ({
    partner: config.partnerName ?? '',
    location: config.locationName ?? '',
    workingUnit:
        config.workingUnitName ||
        [
            config.workingUnitLocation?.workingUnit?.worktype?.worktype,
            config.workingUnitLocation?.workingUnit?.channel?.name,
            config.workingUnitLocation?.workingUnit?.task?.name,
        ]
            .filter(Boolean)
            .join(' - '),
    sisClassification: config.sisClassification ?? '',
});

/**
 * Fresh identifier ColDefs for one grid instance. Returns NEW objects on every call: ag-grid stamps
 * internal state onto colDef objects, so the three grids must not share the same instances.
 *
 * SiS/Service is hidden by default (CALL-751) — its value already shows as a badge on the Working
 * Unit column. Users can reveal the column via the header column menu to sort/filter by it.
 */
export const getIdentifierColumns = (t: TFunction): ColDef[] => [
    { headerName: t('field.partner'), field: 'partner', minWidth: 140, editable: false },
    { headerName: t('field.location'), field: 'location', minWidth: 160, editable: false },
    {
        headerName: t('field.workingUnit'),
        field: 'workingUnit',
        minWidth: 260,
        editable: false,
        cellRenderer: WorkingUnitCell,
    },
    { headerName: t('field.sisService'), field: 'sisClassification', minWidth: 120, editable: false, hide: true },
];
